import type {EnrichedCrossSalePart, StagedCrossSaleLine} from '../types';

export interface SuggestiveSellingStagingState {
    quantities: Map<number, number>;
    selected: Set<number>;
}

export function createEmptyStagingState(): SuggestiveSellingStagingState {
    return {
        quantities: new Map(),
        selected: new Set(),
    };
}

export function normalizeSuggestiveQuantity(quantity: number, allowZero: boolean): number {
    let parsed = Number(quantity);
    if (Number.isNaN(parsed)) {
        parsed = 0;
    }
    parsed = Math.trunc(parsed);
    if (!allowZero) {
        parsed = Math.max(0, parsed);
    }
    return parsed;
}

export function resolveDefaultSourceQuantity(sourceQuantity?: number): number {
    const parsed = Math.trunc(Number(sourceQuantity) || 0);
    return parsed > 0 ? parsed : 1;
}

/** All cross-sale lines selected with the same default quantity as the source add. */
export function createDefaultStaging(
    parts: EnrichedCrossSalePart[],
    sourceQuantity?: number,
): SuggestiveSellingStagingState {
    const defaultQuantity = resolveDefaultSourceQuantity(sourceQuantity);
    const quantities = new Map<number, number>();
    const selected = new Set<number>();
    for (const part of parts) {
        quantities.set(part.partId, defaultQuantity);
        selected.add(part.partId);
    }
    return {quantities, selected};
}

export function isPartSelected(state: SuggestiveSellingStagingState, partId: number): boolean {
    return state.selected.has(partId);
}

export function getPartQuantity(state: SuggestiveSellingStagingState, partId: number): number {
    return state.quantities.get(partId) ?? 0;
}

export function getStagingSummary(state: SuggestiveSellingStagingState): {lineCount: number; totalQty: number} {
    let lineCount = 0;
    let totalQty = 0;
    for (const partId of state.selected) {
        const quantity = state.quantities.get(partId) ?? 0;
        if (quantity > 0) {
            lineCount += 1;
            totalQty += quantity;
        }
    }
    return {lineCount, totalQty};
}

export function buildStagedLinesFromState(
    crossSales: EnrichedCrossSalePart[],
    state: SuggestiveSellingStagingState,
): StagedCrossSaleLine[] {
    const partById = new Map(crossSales.map((part) => [part.partId, part]));
    const lines: StagedCrossSaleLine[] = [];
    for (const partId of state.selected) {
        const quantity = state.quantities.get(partId) ?? 0;
        if (quantity <= 0) {
            continue;
        }
        const part = partById.get(partId);
        if (!part) {
            continue;
        }
        lines.push({
            partId: part.partId,
            partNumber: part.partNumber,
            quantity,
            part,
        });
    }
    return lines;
}

export function applySelectionChange(
    state: SuggestiveSellingStagingState,
    partId: number,
    selected: boolean,
): SuggestiveSellingStagingState {
    const quantities = new Map(state.quantities);
    const selectedIds = new Set(state.selected);

    if (selected) {
        selectedIds.add(partId);
        const current = quantities.get(partId) ?? 0;
        if (current <= 0) {
            quantities.set(partId, 1);
        }
    } else {
        selectedIds.delete(partId);
    }

    return {quantities, selected: selectedIds};
}

export function applyQuantityChange(
    state: SuggestiveSellingStagingState,
    partId: number,
    quantity: number,
    options?: {allowZero?: boolean},
): SuggestiveSellingStagingState {
    const allowZero = options?.allowZero ?? true;
    const normalized = normalizeSuggestiveQuantity(quantity, allowZero);
    const quantities = new Map(state.quantities);
    const selectedIds = new Set(state.selected);

    if (normalized <= 0) {
        quantities.set(partId, 0);
        selectedIds.delete(partId);
    } else {
        quantities.set(partId, normalized);
        selectedIds.add(partId);
    }

    return {quantities, selected: selectedIds};
}
